import React, { FC, useState, useEffect, useCallback, useMemo } from 'react';
import { Typography, Box, CircularProgress, makeStyles, Theme, Paper } from '@material-ui/core';
import { Pagination } from '@material-ui/lab';
import axios, { CancelTokenSource } from 'axios';
import { useIsTabActive } from 'contexts/TabContext';
import useRouter from 'hooks/useRouter';
import { format } from 'date-fns';

// Icons
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';

import {
  EQUIPMENT_BASE_URL,
  GENERATE_SUMMARY_URL,
  GET_ACTIVE_TECHNICIANS_URL,
  GET_AI_SUMMARY_URL,
  GET_SERVICE_ADDRESS_BY_CLIENT_ID_URL,
  JOB_NOTE_BASE_URL
} from 'constants/url';
import ToolBar from './components/ToolBar';
import JobNotesCard from './components/JobNotesCard';
import ActionSnackbar from 'components/ActionSnackbar';
import AiSummaryCard from './components/AiSummaryCard';

const useStyles = makeStyles((theme: Theme) => ({
  paper: {
    margin: 'auto'
  },
  filterBar: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
    marginBottom: theme.spacing(3),
    flexWrap: 'wrap',
    padding: theme.spacing(2),
    backgroundColor: '#f5f5f5',
    borderRadius: theme.spacing(1)
  },
  filterItem: {
    minWidth: 200,
    '&.search': {
      flex: 1,
      minWidth: 250
    }
  },
  clearButton: {
    color: theme.palette.error.main,
    fontSize: '0.75rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    marginLeft: 'auto'
  },
  summaryPanel: {
    marginBottom: theme.spacing(3),
    backgroundColor: '#E8F4F8',
    border: `1px solid #B0D4E3`,
    borderRadius: theme.spacing(1)
  },
  summaryHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(2)
  },
  summaryIcon: {
    color: '#0C7792',
    marginRight: theme.spacing(1)
  },
  summaryTitle: {
    fontWeight: 600,
    color: '#0C7792'
  },
  summarySubtitle: {
    fontSize: '0.875rem',
    color: '#666'
  },
  summaryButtons: {
    display: 'flex',
    gap: theme.spacing(1)
  },
  summaryContent: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: theme.spacing(2),
    padding: theme.spacing(2)
  },
  summarySection: {
    '& h6': {
      fontSize: '0.75rem',
      fontWeight: 700,
      textTransform: 'uppercase',
      color: '#666',
      marginBottom: theme.spacing(1)
    },
    '& ul': {
      margin: 0,
      paddingLeft: theme.spacing(2),
      '& li': {
        fontSize: '0.875rem',
        lineHeight: 1.5,
        marginBottom: theme.spacing(0.5),
        color: '#333'
      }
    }
  },
  noteCard: {
    marginBottom: theme.spacing(2),
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    '&:hover': {
      boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
    }
  },
  noteHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(1)
  },
  noteJobInfo: {
    display: 'flex',
    gap: theme.spacing(3),
    marginBottom: theme.spacing(1)
  },
  noteJobIdBlock: {
    '& label': {
      fontSize: '0.7rem',
      fontWeight: 700,
      textTransform: 'uppercase',
      color: '#999'
    },
    '& a': {
      color: theme.palette.primary.main,
      fontWeight: 600,
      textDecoration: 'none',
      cursor: 'pointer',
      '&:hover': {
        textDecoration: 'underline'
      }
    }
  },
  noteTechnicianInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1)
  },
  noteContent: {
    padding: theme.spacing(2),
    backgroundColor: '#fafafa',
    borderRadius: theme.spacing(0.5),
    marginTop: theme.spacing(1),
    fontSize: '0.875rem',
    lineHeight: 1.6,
    color: '#333',
    fontStyle: 'italic',
    maxHeight: 100,
    overflow: 'hidden'
  },
  noteActions: {
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
    '& button': {
      fontSize: '0.75rem',
      textTransform: 'uppercase'
    }
  },
  sortBy: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginLeft: 'auto'
  },
  emptyState: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 300,
    color: '#999'
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 300
  },
  pagination: {
    bottom: 0,
    zIndex: 2,
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(2),
    marginTop: theme.spacing(3),
    background: '#fff',
    borderTop: '1px solid #eee'
  }
}));

interface AISummary {
  key_issues: string[];
  major_works_done: string[];
  recurring_issues: string[];
  equipment_watch: string[];
  notesCount?: number;
  basedOnRecentNotes?: boolean;
  generatedAt?: Date;
}

interface JobNote {
  id: number;
  jobId: number;
  notes: string;
  createdAt: string;
  displayName: string;
  serviceId: number;
  serviceType?: string;
  serviceAddress?: string;
  Equipments?: any[];
  JobNoteMedia?: any[];
}

interface Props {
  /** Supplied by the page so this tab works inside an app tab, where there is no client route to read. */
  clientId?: string;
}

const JobNotesContent: FC<Props> = props => {
  const isTabActive = useIsTabActive();
  const classes = useStyles();
  const { match } = useRouter();
  const clientId = Number(props.clientId || match.params.id);

  // Filter states - using ColumnFilter pattern
  const [searchText, setSearchText] = useState('');
  const [serviceAddressFilter, setServiceAddressFilter] = useState<ColumnFilter[]>([]);
  const [equipmentFilter, setEquipmentFilter] = useState<ColumnFilter[]>([]);
  const [technicianFilter, setTechnicianFilter] = useState<ColumnFilter[]>([]);
  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [sortBy, setSortBy] = useState('newest');
  const [filterBy, setFilterBy] = useState<string>('');

  // Data states
  const [jobNotes, setJobNotes] = useState<JobNote[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Summary states
  const [summary, setSummary] = useState<AISummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  // Filter master data
  const [serviceAddresses, setServiceAddresses] = useState<Select[]>([]);
  const [technicians, setTechnicians] = useState<Select[]>([]);
  const [equipments, setEquipments] = useState<Select[]>([]);

  // Snackbar states
  const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
  const [snackbarVarient, setSnackbarVarient] = useState<'success' | 'error'>('success');
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(totalCount / pageSize));
  }, [totalCount, pageSize]);

  // Load initial data
  const loadMasterData = useCallback(async () => {
    try {
      const cancelTokenSource: CancelTokenSource = axios.CancelToken.source();

      // Load service addresses
      const { data: addressData } = await axios.get(GET_SERVICE_ADDRESS_BY_CLIENT_ID_URL(clientId), { cancelToken: cancelTokenSource.token });
      const addressList: Select[] = addressData.serviceAddresses.map((addr: any) => ({
        id: addr.id,
        name: addr.address
      }));
      setServiceAddresses(addressList);

      // Load technicians
      const { data: techData } = await axios.get(GET_ACTIVE_TECHNICIANS_URL, { cancelToken: cancelTokenSource.token });
      const techList: Select[] = (techData.activeUsers || []).map((tech: any) => ({
        id: tech.id,
        name: tech.displayName
      }));
      setTechnicians(techList);

      // Load Equipments

      const { data: equipmentData } = await axios.get(`${EQUIPMENT_BASE_URL}?ci=${clientId}`, { cancelToken: cancelTokenSource.token });
      const equipmentList: Select[] = (equipmentData.equipments || []).map((equip: any) => ({
        id: equip.id,
        name: `${equip.brand} ${equip.model}, ${equip.serialNumber}-${equip.location}`
      }));
      setEquipments(equipmentList);
    } catch (err) {
      console.log(err);
    }
  }, [clientId]);

  const getQueryParams = useCallback(
    (targetPage: number = page, noLimit?: boolean) => {
      const params = new URLSearchParams();

      if (searchText) params.append('q', searchText);

      serviceAddressFilter.forEach(value => {
        params.append('sai', value.columnValue.toString());
      });

      equipmentFilter.forEach(value => {
        params.append('ei', value.columnValue.toString());
      });

      technicianFilter.forEach(value => {
        params.append('ti', value.columnValue.toString());
      });

      if (filterBy && (startDate || endDate)) {
        params.append('sd', startDate ? format(new Date(startDate), 'yyyy-MM-dd') : '');
        params.append('ed', endDate ? format(new Date(endDate), 'yyyy-MM-dd') : '');
      }

      params.append('sort', sortBy);
      params.append('ci', clientId.toString());

      if (!noLimit) {
        params.append('s', ((targetPage - 1) * pageSize).toString());
        params.append('l', pageSize.toString());
      }

      return params.toString();
    },
    [searchText, serviceAddressFilter, equipmentFilter, technicianFilter, filterBy, startDate, endDate, sortBy, clientId, page, pageSize]
  );

  const loadJobNotes = useCallback(
    async (targetPage: number = page) => {
      try {
        setLoading(true);

        const cancelTokenSource: CancelTokenSource = axios.CancelToken.source();
        const url = `${JOB_NOTE_BASE_URL}?${getQueryParams(targetPage)}`;

        const { data } = await axios.get(url, {
          cancelToken: cancelTokenSource.token
        });

        setJobNotes(data.rows || []);
        setTotalCount(prev => {
          // keep previous total if backend returns bad value
          if (typeof data.count !== 'number' || data.count === 0) {
            return prev;
          }
          return data.count;
        });
      } catch (err) {
        console.log(err);
      } finally {
        setLoading(false);
      }
    },
    [getQueryParams, page]
  );

  const loadSavedSummary = useCallback(async () => {
    try {
      const { data } = await axios.get(GET_AI_SUMMARY_URL(clientId));

      if (!data) {
        setSummary(null);
        return;
      }

      setSummary(data);
    } catch (err) {
      console.log(err);
    }
  }, [clientId]);

  useEffect(() => {
    loadMasterData();
  }, [loadMasterData]);

  // Load notes when filters change
  useEffect(() => {
    setPage(1);
    loadJobNotes(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText, serviceAddressFilter, equipmentFilter, technicianFilter, startDate, endDate, sortBy, filterBy]);

  useEffect(() => {
    loadJobNotes(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  useEffect(() => {
    loadSavedSummary();
  }, [loadSavedSummary]);

  const generateSummary = async () => {
    try {
      setSummaryLoading(true);

      const { data } = await axios.post(GENERATE_SUMMARY_URL(clientId));

      if (data.error === 'NOT_ENOUGH_NOTES') {
        handleSnackbar('error', 'Not enough notes to generate a meaningful summary. Add more job notes over time.');
      } else {
        setSummary(data);
      }
    } catch (err) {
      handleSnackbar('error', 'Summary could not be generated. Please try again.');
      console.log(err);
    } finally {
      setSummaryLoading(false);
    }
  };

  const copySummary = async () => {
    if (!summary) return;

    const summaryText = `
KEY ISSUES:
${summary.key_issues.map(item => `• ${item}`).join('\n')}

MAJOR WORKS DONE:
${summary.major_works_done.map(item => `• ${item}`).join('\n')}

RECURRING ISSUES:
${summary.recurring_issues.map(item => `• ${item}`).join('\n')}

EQUIPMENT WATCH:
${summary.equipment_watch.map(item => `• ${item}`).join('\n')}
    `.trim();

    try {
      await navigator.clipboard.writeText(summaryText);
      alert('Summary copied to clipboard!');
    } catch (err) {
      console.log(err);
    }
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, newPage: number) => {
    if (newPage === page) return;

    setPage(newPage);

    const notesContainer = document.getElementById('job-notes-list');
    if (notesContainer) {
      notesContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleSnackbar = (variant: 'success' | 'error', snackbarMessage: string) => {
    setOpenSnackbar(true);
    setSnackbarVarient(variant);
    setSnackbarMessage(snackbarMessage);
  };

  return (
    <Paper variant='outlined' className={classes.paper}>
      {/* Filter Bar */}
      <ToolBar
        searchText={searchText}
        setSearchText={setSearchText}
        serviceAddressFilter={serviceAddressFilter}
        setServiceAddressFilter={setServiceAddressFilter}
        equipmentFilter={equipmentFilter}
        setEquipmentFilter={setEquipmentFilter}
        technicianFilter={technicianFilter}
        setTechnicianFilter={setTechnicianFilter}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        serviceAddresses={serviceAddresses}
        technicians={technicians}
        equipments={equipments}
        filterBy={filterBy}
        setFilterBy={setFilterBy}
        sortBy={sortBy}
        setSortBy={setSortBy}
      />

      {/* AI Summary Panel */}
      <AiSummaryCard
        summary={
          summary || {
            key_issues: [],
            major_works_done: [],
            recurring_issues: [],
            equipment_watch: [],
            notesCount: 0,
            basedOnRecentNotes: false,
            generatedAt: new Date()
          }
        }
        loading={summaryLoading}
        lastUpdatedText={
          summary?.generatedAt ? `Last updated ${format(new Date(summary.generatedAt), 'dd MMM yyyy HH:mm')}` : 'Summary not generated yet'
        }
        onCopy={copySummary}
        onRegenerate={generateSummary}
      />

      {/* Job Notes Feed */}
      <Box id='job-notes-list'>
        {loading ? (
          <Box className={classes.loadingContainer}>
            <CircularProgress />
          </Box>
        ) : jobNotes.length === 0 ? (
          <Box className={classes.emptyState}>
            <Typography>No job notes found for this client</Typography>
          </Box>
        ) : (
          <>
            {jobNotes.map(note => (
              <JobNotesCard
                key={note.id}
                note={{
                  id: note.id,
                  jobId: note.jobId,
                  serviceType: note.serviceType || 'Service',
                  serviceAddress: note.serviceAddress || 'Service Address',
                  serviceId: note.serviceId,
                  technicianName: note.displayName,
                  notes: note.notes,
                  createdAt: note.createdAt,
                  photoCount: note.JobNoteMedia?.length || 0,
                  JobNoteMedia: note.JobNoteMedia || [],
                  Equipments: note.Equipments || []
                }}
                handleSnackbar={handleSnackbar}
              />
            ))}
          </>
        )}
      </Box>

      {totalPages > 1 && (
        <Box className={classes.pagination}>
          <Pagination count={totalPages} page={page} onChange={handlePageChange} color='primary' disabled={loading} showFirstButton showLastButton />
        </Box>
      )}

      {openSnackbar && (
        <ActionSnackbar
          variant={snackbarVarient}
          message={snackbarMessage}
          open={isTabActive && openSnackbar}
          handleClose={() => setOpenSnackbar(false)}
          Icon={snackbarVarient === 'success' ? CheckCircleIcon : ErrorIcon}
        />
      )}
    </Paper>
  );
};

export default JobNotesContent;
